import React from 'react';
import { Box, Chip, Tooltip, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-pro';
import CreativeCell from './components/CreativeCell';
import { CreativeRow, GroupBy, MediaFilter } from './types';
import { MetricColOptions, MetricText, buildMetricCol } from './metricCol';
import {
  fmtInt,
  fmtMoney,
  fmtPct,
  fmtPremium,
  fmtRatio,
  fmtSeconds,
  fmtDate,
  premiumMath,
  premiumTone,
  profitTone,
  roasTone,
  trueProfitMath,
  trueRevenueMath,
  trueRoasMath,
} from './utils';

type Formatter = (value: unknown) => string;

type MetricOptions = MetricColOptions<CreativeRow>;

function metricCol(
  field: keyof CreativeRow & string,
  headerName: string,
  format: Formatter,
  options: MetricOptions = {},
): GridColDef {
  return buildMetricCol<CreativeRow>(field, headerName, format, options);
}

function textCol(
  field: keyof CreativeRow,
  headerName: string,
  width: number,
  variantField?: keyof CreativeRow,
  onVariants?: (row: CreativeRow, assetType: 'headline' | 'primary_text') => void,
): GridColDef {
  return {
    field,
    headerName,
    width,
    sortable: true,
    valueGetter: params => (params.row as CreativeRow)[field] ?? '',
    renderCell: params => {
      const row = params.row as CreativeRow;
      const text = (params.value as string) || '';
      const variants = variantField ? Number(row[variantField]) || 1 : 1;
      if (!text) return <MetricText value="–" />;
      return (
        <Tooltip title={text} placement="top-start">
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                lineHeight: 1.3,
              }}
            >
              {text}
            </Typography>
            {variants > 1 && (
              // Advantage+ rotates several variants per ad, so a single value
              // here is the leading one, not the only one. Click to see the rest
              // with what each of them earned.
              <Typography
                variant="caption"
                onClick={event => {
                  event.stopPropagation();
                  onVariants?.(
                    row,
                    field === 'headline' ? 'headline' : 'primary_text',
                  );
                }}
                sx={{
                  color: '#6366F1',
                  cursor: onVariants ? 'pointer' : 'default',
                  '&:hover': { textDecoration: onVariants ? 'underline' : 'none' },
                }}
              >
                +{variants - 1} more variant{variants - 1 === 1 ? '' : 's'}
              </Typography>
            )}
          </Box>
        </Tooltip>
      );
    },
  };
}

export function buildColumns(
  groupBy: GroupBy,
  mediaType: MediaFilter,
  onPreview: (row: CreativeRow) => void,
  onVariants: (row: CreativeRow, assetType: 'headline' | 'primary_text') => void,
  // The incrementality multiplier, as served by the API. Null until the first
  // response lands — the hover math then simply omits itself rather than this
  // page keeping its own copy of the number and risking a disagreement.
  multiplier: number | null,
): GridColDef[] {
  // Every measure is built; which ones are on screen is the Columns dialog's
  // job, so there is one mechanism for that rather than two.
  // Video columns are dead weight when the table is filtered to statics.
  const showVideo = mediaType !== 'static' && mediaType !== 'carousel';
  // A rollup row averages several ads' frequency, which is worth saying in the
  // premium header — it makes that number a floor rather than an exact figure.
  const isRollupView = groupBy !== 'ad';

  const columns: GridColDef[] = [
    {
      field: 'group_key',
      headerName:
        groupBy === 'creative'
          ? 'Creative asset'
          : groupBy === 'headline'
          ? 'Headline'
          : groupBy === 'primary_text'
          ? 'Primary text'
          : 'Ad',
      width: 340,
      sortable: false,
      // The cell renders from the row, so this value is only ever read by the
      // CSV export — where the raw group_key at ad level is a bare numeric ad
      // id and names nothing. Mirrors CreativeCell's own title so the file says
      // what the screen says.
      valueGetter: params => {
        const row = params.row as CreativeRow;
        if (groupBy === 'headline') return row.headline || '(no headline)';
        if (groupBy === 'primary_text') return row.primary_text || '(no primary text)';
        return row.ad_name || row.group_key;
      },
      renderCell: params => (
        <CreativeCell
          row={params.row as CreativeRow}
          groupBy={groupBy}
          onPreview={onPreview}
        />
      ),
    },
  ];

  if (groupBy !== 'headline') {
    columns.push(
      textCol('headline', 'Headline', 260, 'headline_variant_count', onVariants),
    );
  }
  if (groupBy !== 'primary_text') {
    columns.push(
      textCol(
        'primary_text',
        'Primary text',
        400,
        'primary_text_variant_count',
        onVariants,
      ),
    );
  }

  // Where the row sits in the account. Hidden by default (see
  // DEFAULT_HIDDEN_COLUMNS): the identity cell already names the ad and prints
  // the campaign under it, so on screen these are duplication. They exist as
  // columns because the CSV export carries the visible columns and nothing
  // else — without them an export is ad names and numbers with no way to tell
  // which campaign earned what. On a rollup row they describe the
  // highest-spending ad in the group, like every other attribute here.
  columns.push(
    textCol('campaign_name', 'Campaign', 240),
    textCol('adset_name', 'Ad set', 220),
    textCol('ad_name', 'Ad name', 240),
  );

  columns.push({
    field: 'effective_status',
    headerName: 'Status',
    width: 128,
    sortable: true,
    valueGetter: params => (params.row as CreativeRow).effective_status ?? '',
    renderCell: params => {
      const status = String(params.value || '');
      if (!status) return <MetricText value="–" />;
      const active = status === 'ACTIVE';
      return (
        <Chip
          label={status.replace(/_/g, ' ').toLowerCase()}
          size="small"
          sx={{
            height: 20,
            fontSize: 11,
            color: active ? '#059669' : '#64748B',
            backgroundColor: active
              ? 'rgba(5, 150, 105, 0.10)'
              : 'rgba(100, 116, 139, 0.10)',
          }}
        />
      );
    },
  });

  columns.push(
    // Not a metric — a date — but it aligns like one.
    metricCol('created_time', 'Started', fmtDate, {
      width: 124,
      help: 'When the ad was created in Meta',
      sortAs: 'date',
    }),
    metricCol('spend', 'Spend', fmtMoney),
  );

  columns.push(
    metricCol('revenue', 'Revenue', fmtMoney, {
      help: 'Triple Whale attributed revenue',
    }),
    metricCol('profit', 'Profit', fmtMoney, {
      tone: row => profitTone(row.profit),
      help: 'Revenue minus ad spend, COGS and shipping',
    }),
    metricCol('roas', 'ROAS', fmtRatio, {
      width: 96,
      tone: row => roasTone(row.roas),
      help: 'Triple Whale revenue ÷ spend',
    }),
    metricCol('poas', 'POAS', fmtRatio, {
      width: 96,
      tone: row => profitTone(row.poas),
      help: 'Profit ÷ spend. Below 0 means the ad lost money.',
    }),
    // Sits immediately left of the true-* money: premium is how you read that
    // money. Strong revenue on a low premium is a creative still finding fresh
    // audience; the same revenue on a high premium is the same people again.
    metricCol('premium', 'Premium', fmtPremium, {
      width: 112,
      tone: row => premiumTone(row.premium),
      help:
        'How much of this ad’s delivery went to people who had already seen it. ' +
        'Premium is Meta’s frequency minus 1, so 0% means everyone who saw it saw it once and every dollar was still reaching someone new, while 30% means you paid for 1.3 impressions per person — roughly 3 in every 10 were repeats. ' +
        'Lower is better: a low premium alongside a healthy True ROAS is a creative still finding new audience cheaply, and a climbing premium means you are buying the same eyeballs again and it is time to refresh. Above 15% is where the Meta Ads Insights dashboard treats the audience as tiring. ' +
        (isRollupView
          ? 'This row rolls up several ads, so it is the impression-weighted average of theirs — and a floor, since someone reached by two of the ads is counted twice.'
          : ''),
      cellHelp: premiumMath,
    }),
    metricCol('true_revenue', 'True revenue', fmtMoney, {
      width: 132,
      help: `Meta's reported purchase value × ${multiplier ??
        'the incrementality multiplier'}, the uplift measured in the Meta incrementality test. Hover a value to see the arithmetic.`,
      cellHelp: row => trueRevenueMath(row, multiplier),
    }),
    metricCol('true_profit', 'True profit', fmtMoney, {
      width: 126,
      tone: row => profitTone(row.true_profit),
      help:
        'True revenue charged at Triple Whale’s margin, minus ad spend — the uplift is extra orders, so it carries product cost too. Blank when Meta reported no purchases for the row (nothing to uplift, same rows where Meta ROAS is blank) or when Triple Whale recorded no revenue to take a margin rate from.',
      cellHelp: row => trueProfitMath(row, multiplier),
    }),
    metricCol('true_roas', 'True ROAS', fmtRatio, {
      width: 116,
      tone: row => roasTone(row.true_roas),
      help: `Meta-reported ROAS × ${multiplier ??
        'the incrementality multiplier'}. The same definition the Meta Ads Insights dashboard uses, so an ad reads the same on both pages.`,
      cellHelp: row => trueRoasMath(row, multiplier),
    }),
    metricCol('orders', 'Orders', fmtInt, { width: 104 }),
    metricCol('cpa', 'CPA', fmtMoney, { width: 96 }),
    metricCol('aov', 'AOV', fmtMoney, { width: 96 }),
    metricCol('nc_roas', 'NC ROAS', fmtRatio, {
      width: 116,
      help: 'New-customer revenue ÷ spend',
    }),
    metricCol('nc_cpa', 'NC CPA', fmtMoney, {
      width: 112,
      help: 'Spend ÷ new-customer orders',
    }),
    metricCol('meta_roas', 'Meta ROAS', fmtRatio, {
      width: 124,
      help: 'ROAS as Meta reports it, from its own pixel attribution',
    }),
    metricCol('meta_purchases', 'Meta purch.', fmtInt, {
      width: 124,
      help: 'Purchases as Meta reports them',
    }),
    metricCol('attribution_gap', 'Attr. gap', fmtMoney, {
      width: 118,
      help:
        'Triple Whale revenue minus Meta-reported purchase value. Negative means Meta claims more revenue than Triple Whale can attribute.',
    }),
  );

  columns.push(
    metricCol('sessions', 'Sessions', fmtInt, { width: 118 }),
    metricCol('cvr', 'CVR', v => fmtPct(v, 2), {
      width: 96,
      help: 'Orders ÷ sessions',
    }),
    metricCol('atc_rate', 'ATC rate', v => fmtPct(v, 1), {
      width: 116,
      help: 'Add-to-carts ÷ sessions',
    }),
    metricCol('ctr', 'CTR', v => fmtPct(v, 2), { width: 96 }),
    metricCol('cpm', 'CPM', fmtMoney, { width: 96 }),
    metricCol('impressions', 'Impr.', fmtInt, { width: 110 }),
    metricCol('reach', 'Reach', fmtInt, {
      width: 110,
      help:
        'People reached, deduplicated across the whole date range by Meta — not a sum of daily reach, which would count the same person once per day.',
    }),
    metricCol('frequency', 'Frequency', fmtRatio, {
      width: 118,
      help: 'Impressions ÷ people reached, over the whole date range.',
    }),
  );

  if (showVideo) {
    columns.push(
      metricCol('hold_rate', '25% watched', v => fmtPct(v, 1), {
        width: 110,
        help:
          'Viewers who watched at least 25% of the video, as a share of impressions. Meta removed the 3-second metric in API v24, so this is the closest available stand-in for a hook rate.',
      }),
      metricCol('thruplay_rate', 'ThruPlay', v => fmtPct(v, 1), {
        width: 116,
        help: 'ThruPlays (15s or full watch) ÷ impressions',
      }),
      metricCol('completion_rate', 'Completed', v => fmtPct(v, 1), {
        width: 122,
        help: 'Watched to 100% ÷ impressions',
      }),
      metricCol('avg_watch_time', 'Avg watch', fmtSeconds, {
        width: 118,
        help: 'Average seconds watched, weighted by impressions',
      }),
    );
  }

  return columns;
}
